import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "@/components/TopNav";
import BackgroundRecordsCard from "@/components/BackgroundRecordsCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CircleDollarSign, ShieldCheck, Wallet, ArrowRight, Building2, CalendarDays, FileText, Bell, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AGENCY, NAV } from "@/constants/testIds";

export default function AgencyDashboard() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [stripeStatus, setStripeStatus] = useState(null);
    const [shifts, setShifts] = useState([]);
    const [newShift, setNewShift] = useState({
        title: "", location: "", start: "", end: "", rate_cents: "", notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [aiHint, setAiHint] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [aiSkills, setAiSkills] = useState([]);

    async function load() {
        try {
            const [s, sh] = await Promise.all([
                api.get("/stripe/status"),
                api.get("/shifts"),
            ]);
            setStripeStatus(s.data);
            setShifts(sh.data);
        } catch (e) {
            // ignore
        }
    }

    useEffect(() => { load(); }, []);

    async function onboardStripe() {
        setBusy(true);
        try {
            const { data } = await api.post("/stripe/onboard");
            if (data.url) {
                window.location.href = data.url;
            } else {
                toast.success("Stripe onboarding simulated (dev mode)");
                await refresh();
                await load();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Stripe error");
        } finally {
            setBusy(false);
        }
    }

    async function submitShift(e) {
        e.preventDefault();
        try {
            const payload = {
                title: newShift.title,
                location: newShift.location,
                start: new Date(newShift.start).toISOString(),
                end: new Date(newShift.end).toISOString(),
                rate_cents: Math.round(parseFloat(newShift.rate_cents) * 100),
                notes: newShift.notes || "",
            };
            await api.post("/shifts", payload);
            toast.success("Shift posted");
            setNewShift({ title: "", location: "", start: "", end: "", rate_cents: "", notes: "" });
            setAiHint("");
            setAiSkills([]);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    }

    async function writeDescription() {
        if (!aiHint.trim()) return toast.error("Type a short hint first (e.g. 'overnight, senior with mild dementia')");
        setAiBusy(true);
        try {
            const { data } = await api.post("/ai/describe-shift", { hint: aiHint });
            setNewShift((s) => ({ ...s, notes: data.description }));
            setAiSkills(data.required_skills || []);
            toast.success("Automated writer drafted your description");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Automated writer failed");
        } finally {
            setAiBusy(false);
        }
    }

    const onboarded = stripeStatus?.onboarded;

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid={AGENCY.dashboard} className="max-w-7xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Agency workspace</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                        Hello, {user?.agency_name || user?.full_name}
                    </h1>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Badge data-testid={AGENCY.einBadge} className="bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-50">
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> EIN {user?.ein}
                        </Badge>
                        <Badge
                            data-testid={AGENCY.stripeStatusBadge}
                            className={onboarded
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-50"
                                : "bg-orange-50 text-orange-700 border border-orange-100 hover:bg-orange-50"}
                        >
                            <Wallet className="w-3.5 h-3.5 mr-1" />
                            Stripe: {onboarded ? "Connected" : "Not connected"}
                            {stripeStatus?.mocked && <span className="ml-1 opacity-60">(dev)</span>}
                        </Badge>
                    </div>
                </div>

                <div className="mb-8">
                    <BackgroundRecordsCard />
                </div>

                <div className="grid lg:grid-cols-3 gap-6 mb-8">
                    <Card className="rounded-2xl border-stone-200 lg:col-span-2 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white overflow-hidden relative grain-overlay">
                        <CardContent className="p-8 relative z-10">
                            <div className="flex items-center gap-2 text-emerald-200 text-xs font-bold uppercase tracking-[0.2em]">
                                <CircleDollarSign className="w-4 h-4" /> Stripe Connect Express
                            </div>
                            <h2 className="font-outfit text-2xl sm:text-3xl font-semibold mt-2">
                                {onboarded ? "You’re payout-ready." : "Complete Stripe onboarding to send payouts."}
                            </h2>
                            <p className="text-emerald-100/90 mt-2 text-sm font-dmsans max-w-xl">
                                We collect your business info via Stripe's hosted flow. Choose cadence and method on the Payments panel.
                            </p>
                            <div className="mt-6 flex gap-3">
                                {!onboarded && (
                                    <Button
                                        data-testid={AGENCY.onboardStripeBtn}
                                        onClick={onboardStripe}
                                        disabled={busy}
                                        className="rounded-full bg-white text-emerald-800 hover:bg-emerald-50 px-6"
                                    >
                                        {busy ? "..." : "Onboard with Stripe"} <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                )}
                                <Button
                                    onClick={() => nav("/payments")}
                                    variant="outline"
                                    className="rounded-full bg-transparent border-emerald-300/50 text-white hover:bg-white/10"
                                >
                                    Payment settings
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <QuickLinks />
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-emerald-700" /> Post a shift
                            </h3>
                            <form onSubmit={submitShift} className="space-y-3">
                                <Input
                                    data-testid={AGENCY.shiftTitleInput}
                                    placeholder="Shift title (e.g. Overnight Home Care)"
                                    value={newShift.title}
                                    onChange={(e) => setNewShift({ ...newShift, title: e.target.value })}
                                    required
                                    className="h-11 rounded-xl"
                                />
                                <Input
                                    data-testid={AGENCY.shiftLocationInput}
                                    placeholder="Location (e.g. Brooklyn, NY)"
                                    value={newShift.location}
                                    onChange={(e) => setNewShift({ ...newShift, location: e.target.value })}
                                    required
                                    className="h-11 rounded-xl"
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        data-testid={AGENCY.shiftStartInput}
                                        type="datetime-local"
                                        value={newShift.start}
                                        onChange={(e) => setNewShift({ ...newShift, start: e.target.value })}
                                        required
                                        className="h-11 rounded-xl"
                                    />
                                    <Input
                                        data-testid={AGENCY.shiftEndInput}
                                        type="datetime-local"
                                        value={newShift.end}
                                        onChange={(e) => setNewShift({ ...newShift, end: e.target.value })}
                                        required
                                        className="h-11 rounded-xl"
                                    />
                                </div>
                                <Input
                                    data-testid={AGENCY.shiftRateInput}
                                    type="number"
                                    step="0.01"
                                    placeholder="Rate in USD (e.g. 180.00)"
                                    value={newShift.rate_cents}
                                    onChange={(e) => setNewShift({ ...newShift, rate_cents: e.target.value })}
                                    required
                                    className="h-11 rounded-xl"
                                />

                                {/* Automated writer */}
                                <div className="rounded-xl border border-dashed border-emerald-200 bg-emerald-50/40 p-3">
                                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-2">
                                        <Sparkles className="w-3.5 h-3.5" /> Automated shift writer
                                    </div>
                                    <div className="flex gap-2">
                                        <Input
                                            data-testid="agency-ai-hint-input"
                                            placeholder="Hint: e.g. 'overnight senior care, mild dementia'"
                                            value={aiHint}
                                            onChange={(e) => setAiHint(e.target.value)}
                                            className="h-10 rounded-lg bg-white"
                                        />
                                        <Button
                                            data-testid="agency-ai-write-btn"
                                            type="button"
                                            onClick={writeDescription}
                                            disabled={aiBusy}
                                            variant="outline"
                                            className="rounded-full border-emerald-200 text-emerald-800 hover:bg-emerald-100 h-10 shrink-0"
                                        >
                                            {aiBusy ? "…" : "Write"}
                                        </Button>
                                    </div>
                                    {newShift.notes && (
                                        <div className="mt-2 p-3 rounded-lg bg-white border border-emerald-100 text-sm text-stone-700 font-dmsans">
                                            {newShift.notes}
                                        </div>
                                    )}
                                    {aiSkills.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {aiSkills.map((s) => (
                                                <span key={s} className="text-xs bg-white text-emerald-800 border border-emerald-100 rounded-full px-2.5 py-0.5">
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <Button
                                    data-testid={AGENCY.shiftSubmit}
                                    type="submit"
                                    className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white w-full h-11"
                                >
                                    Post shift
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4">Recent shifts</h3>
                            <div className="space-y-3 max-h-[420px] overflow-y-auto">
                                {shifts.length === 0 && (
                                    <p className="text-sm text-stone-500 font-dmsans">No shifts posted yet.</p>
                                )}
                                {shifts.map((s) => (
                                    <div key={s.id} className="p-4 border border-stone-200 rounded-xl hover:bg-stone-50 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-medium text-stone-900">{s.title}</div>
                                                <div className="text-xs text-stone-500 font-dmsans">{s.location}</div>
                                            </div>
                                            <Badge className={
                                                s.status === "open" ? "bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-50" :
                                                s.status === "claimed" ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50" :
                                                "bg-stone-100 text-stone-700 border-stone-200 hover:bg-stone-100"
                                            }>
                                                {s.status}
                                            </Badge>
                                        </div>
                                        <div className="mt-2 text-xs text-stone-500 font-dmsans">
                                            ${((s.rate_cents || 0) / 100).toFixed(2)} · {new Date(s.start).toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
}

function QuickLinks() {
    const nav = useNavigate();
    const items = [
        { icon: Wallet, label: "Payment settings", to: "/payments", key: NAV.paymentsLink },
        { icon: CalendarDays, label: "Facility schedule", to: "/facility", key: "facility-link" },
        { icon: ShieldCheck, label: "Background monitor", to: "/monitor", key: "monitor-link" },
        { icon: FileText, label: "Document vault", to: "/vault", key: NAV.vaultLink },
        { icon: CalendarDays, label: "Shifts", to: "/shifts", key: NAV.shiftsLink },
        { icon: Bell, label: "Notifications", to: "/notify", key: NAV.notifyLink },
    ];
    return (
        <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
                <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-4">Quick actions</h3>
                <div className="space-y-2">
                    {items.map((it) => {
                        const Icon = it.icon;
                        return (
                            <button
                                key={it.label}
                                onClick={() => nav(it.to)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 border border-transparent hover:border-stone-200 transition-all text-left"
                            >
                                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                                    <Icon className="w-4 h-4 text-emerald-700" strokeWidth={1.75} />
                                </div>
                                <span className="text-sm font-medium text-stone-800 font-dmsans">{it.label}</span>
                                <ArrowRight className="w-4 h-4 text-stone-400 ml-auto" />
                            </button>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}
